import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { STORE_LOCATION, ORDER_STATUSES } from '../data';
import './OrderTracking.css';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createCustomIcon = (emoji, size = 40) => L.divIcon({
  className: '',
  html: `<div style="font-size:${size}px;line-height:1;filter:drop-shadow(0 2px 6px rgba(0,0,0,0.3));cursor:default;">${emoji}</div>`,
  iconSize:   [size, size],
  iconAnchor: [size / 2, size / 2],
});

function interpolate(p1, p2, t) {
  return {
    lat: p1.lat + (p2.lat - p1.lat) * t,
    lng: p1.lng + (p2.lng - p1.lng) * t,
  };
}

// Auto-fit map bounds — runs once on mount to fit both markers
function FitBounds({ positions }) {
  const map = useMap();
  const posRef = useRef(positions);

  useEffect(() => {
    const pts = posRef.current;
    if (pts.length > 1) {
      const bounds = L.latLngBounds(pts.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [map]);
  return null;
}

const STATUS_DURATION = [0, 8000, 16000, 24000, 35000];

export default function OrderTracking({ navigate, order }) {
  const [currentStatusIdx, setCurrentStatusIdx] = useState(0);
  const [riderPos,         setRiderPos]         = useState(STORE_LOCATION);
  const [elapsed,          setElapsed]          = useState(0);
  const startTime = useRef(Date.now());
  const animRef   = useRef(null);

  const deliveryLoc = order?.deliveryLocation || { lat: 18.5204, lng: 73.8400, name: 'Your Location' };
  const midPoint    = {
    lat: (STORE_LOCATION.lat + deliveryLoc.lat) / 2 + 0.005,
    lng: (STORE_LOCATION.lng + deliveryLoc.lng) / 2,
  };
  const routeWaypoints = [STORE_LOCATION, midPoint, deliveryLoc];

  const tick = useCallback(() => {
    const now = Date.now();
    const el  = now - startTime.current;
    setElapsed(el);

    let sIdx = 0;
    for (let i = STATUS_DURATION.length - 1; i >= 0; i--) {
      if (el >= STATUS_DURATION[i]) { sIdx = i; break; }
    }
    setCurrentStatusIdx(sIdx);

    if (sIdx >= 2) {
      const rideStart    = STATUS_DURATION[2];
      const rideEnd      = STATUS_DURATION[4];
      const rideElapsed  = el - rideStart;
      const rideDuration = rideEnd - rideStart;
      const t = Math.min(rideElapsed / rideDuration, 1);
      const pos = t < 0.5
        ? interpolate(STORE_LOCATION, midPoint, t * 2)
        : interpolate(midPoint, deliveryLoc, (t - 0.5) * 2);
      setRiderPos(pos);
    }

    if (sIdx < ORDER_STATUSES.length - 1) {
      animRef.current = requestAnimationFrame(tick);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [tick]);

  const isDelivered     = currentStatusIdx === ORDER_STATUSES.length - 1;
  const remainingSeconds = Math.max(0, Math.round((STATUS_DURATION[4] - elapsed) / 1000));
  const remainingMin    = Math.floor(remainingSeconds / 60);
  const remainingSec    = remainingSeconds % 60;

  return (
    <div className="tracking-page">
      {/* Header */}
      <div className="tracking-header">
        <button className="back-btn" onClick={() => navigate('home')}>🌿 FreshMart</button>
        <div className="order-id-badge">Order #{order?.id || 'FM' + Date.now()}</div>
        <button className="back-btn ghost" onClick={() => navigate('products')}>🛒 Shop More</button>
      </div>

      <div className="tracking-layout">
        {/* Left Panel */}
        <div className="tracking-panel">
          {!isDelivered ? (
            <div className="eta-card">
              <div className="eta-icon">🛵</div>
              <div className="eta-info">
                <p className="eta-label">Estimated Arrival</p>
                <p className="eta-time">
                  {remainingMin > 0 ? `${remainingMin} min ${remainingSec} sec` : `${remainingSec} sec`}
                </p>
              </div>
              <div className="eta-pulse" />
            </div>
          ) : (
            <div className="delivered-card">
              <div className="delivered-emoji">🎉</div>
              <div>
                <p className="delivered-title">Delivered!</p>
                <p className="delivered-sub">Enjoy your fresh groceries!</p>
              </div>
            </div>
          )}

          {/* Status Timeline */}
          <div className="status-timeline">
            <h3>Order Status</h3>
            {ORDER_STATUSES.map((s, i) => (
              <div
                key={s.key}
                className={`status-step ${i < currentStatusIdx ? 'done' : ''} ${i === currentStatusIdx ? 'active' : ''}`}
              >
                <div className="status-dot-wrap">
                  <div className="status-dot">
                    {i < currentStatusIdx ? '✓' : s.icon}
                  </div>
                  {i < ORDER_STATUSES.length - 1 && <div className="status-line" />}
                </div>
                <div className="status-text">
                  <p className="status-label">{s.label}</p>
                  <p className="status-desc">{s.desc}</p>
                  {i === currentStatusIdx && (
                    <span className="status-live-badge">● LIVE</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="order-mini-summary">
            <h3>Your Order</h3>
            {order?.items?.slice(0, 5).map((item, i) => (
              <div key={i} className="mini-item">
                <span>{item.image}</span>
                <span className="mi-name">{item.name}</span>
                <span className="mi-qty">×{item.qty}</span>
                <span className="mi-price">₹{item.price * item.qty}</span>
              </div>
            ))}
            {order?.items?.length > 5 && (
              <p className="more-items-text">+{order.items.length - 5} more items</p>
            )}
            <div className="order-total-row">
              <span>Total Paid</span>
              <span className="order-total-amt">₹{order?.total?.toLocaleString()}</span>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="delivery-info-card">
            <div className="di-row">
              <span>📍</span>
              <div>
                <p className="di-label">Delivery Address</p>
                <p className="di-value">{order?.customer?.address || 'Your address'}, {order?.customer?.city}</p>
              </div>
            </div>
            <div className="di-row">
              <span>💳</span>
              <div>
                <p className="di-label">Payment</p>
                <p className="di-value">
                  {order?.paymentMethod === 'cod'  ? 'Cash on Delivery' :
                   order?.paymentMethod === 'upi'  ? 'UPI Payment' : 'Card Payment'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="map-container">
          <div className="map-overlay-badge">
            <span>🗺️</span> Live Tracking Map
          </div>
          <MapContainer
            center={[STORE_LOCATION.lat, STORE_LOCATION.lng]}
            zoom={13}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            <FitBounds positions={[STORE_LOCATION, deliveryLoc]} />

            <Marker position={[STORE_LOCATION.lat, STORE_LOCATION.lng]} icon={createCustomIcon('🏪', 36)}>
              <Popup><strong>FreshMart Store</strong><br />{STORE_LOCATION.name}</Popup>
            </Marker>

            <Marker position={[deliveryLoc.lat, deliveryLoc.lng]} icon={createCustomIcon('🏠', 36)}>
              <Popup><strong>Your Location</strong><br />{order?.customer?.address}</Popup>
            </Marker>

            {currentStatusIdx >= 2 && (
              <Marker position={[riderPos.lat, riderPos.lng]} icon={createCustomIcon('🛵', 44)}>
                <Popup><strong>Your Rider</strong><br />On the way!</Popup>
              </Marker>
            )}

            <Polyline
              positions={routeWaypoints.map(p => [p.lat, p.lng])}
              color="#1DB954"
              weight={4}
              opacity={0.7}
              dashArray={currentStatusIdx >= 2 ? undefined : '8 8'}
            />
          </MapContainer>

          <div className="map-legend">
            <div className="legend-item"><span>🏪</span> Store</div>
            <div className="legend-item"><span>🛵</span> Rider</div>
            <div className="legend-item"><span>🏠</span> You</div>
          </div>
        </div>
      </div>
    </div>
  );
}
